import React from 'react';
import {
   DefaultValues,
   FieldErrors,
   FieldValues,
   FormProvider,
   KeepStateOptions,
   Path,
   SubmitErrorHandler,
   useForm,
   UseFormProps,
   UseFormReset,
} from 'react-hook-form';
import {ObjectSchema} from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {cloneDeep, get, isEmpty, set} from 'lodash';
import {
   setErrorsFactory,
   setSubmittingFactory,
   SetValueOptions,
} from 'components/hook-form/helpers';
import {FormStatusContextProvider} from 'common/context/FormStatusContext';
import {combineYupErrorsMessages, ErrorMessageOut} from 'common/yup/combineYupErrorsMessages';
import {useToasts} from 'features/notifications/useToasts';
import {FormContextProvider, useFormContextBuilder} from 'common/context/FormContext';

export interface FormOnSubmitHelpers<T extends FieldValues> {
   setSubmitting: (state: boolean) => void;
   setErrors: (errors: any) => void;
   resetForm: UseFormReset<T>; //UseFormReset<T extends FieldValues ? FormValues : FormValues>;
   setStatus: (status: any) => void;
}

export interface FormOnSubmitOutsideHelpers<T> extends FormOnSubmitHelpers<FieldValues> {
   getValues: () => T;
}

interface FormProps<T extends FieldValues = FieldValues> {
   initialValues: UseFormProps<T>['defaultValues'];
   initialStatus?: any;
   enableReinitialize?: boolean;
   validationSchema?: ObjectSchema;
   //    validateOnChange: boolean;
   validate?: (values: T) => Object;
   onSubmit: (values: T, helpers: FormOnSubmitHelpers<T>) => void | Promise<void | T>;
   onError?: SubmitErrorHandler<T>; // (errors: T) => void | Promise<T>;
   onReset?: (values?: DefaultValues<T> | T | ((formValues: T) => T)) => void;
   children: React.ReactNode;
   initialTouched?: Path<T>[];
   passThrough?: any;
}
function Formik<T extends FieldValues = FieldValues>({
   initialValues,
   initialStatus,
   enableReinitialize,
   validationSchema,
   onSubmit,
   onError,
   onReset,
   validate,
   children,
   initialTouched,
   passThrough,
}: FormProps<T>) {
   const {addToast} = useToasts();
   const defaultValues = React.useMemo(() => {
      const vals = cloneDeep(initialValues);
      initialTouched?.forEach((iT) => set<any>(vals, iT, undefined));

      return vals;
   }, [initialValues, initialTouched]);

   const methods = useForm<T>({
      defaultValues,
      resolver: validationSchema && yupResolver(validationSchema),
   });
   const {
      handleSubmit,
      watch,
      setValue,
      reset: _reset,
      setError,
      control: {_fields},
   } = methods;
   const statusState = React.useState(initialStatus);
   const [, setStatus] = statusState;

   const formContext = useFormContextBuilder(passThrough);
   const {formRef, registered} = formContext;

   const reset = React.useCallback(
      (values?: DefaultValues<T> | T | ((formValues: T) => T), keepOptions?: KeepStateOptions) => {
         console.debug('reset', values, initialValues, keepOptions);
         _reset(values || (cloneDeep(initialValues) as T), keepOptions);
         onReset?.(values);
      },
      [_reset, initialValues, onReset]
   );

   const _onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      // this part is for stopping parent forms to trigger their submit
      if (event) {
         // sometimes not true, e.g. React Native
         if (typeof event.preventDefault === 'function') {
            event.preventDefault();
         }
         if (typeof event.stopPropagation === 'function') {
            // prevent any outer forms from receiving the event too
            event.stopPropagation();
         }
      }

      const setErrors = setErrorsFactory(setError, setValue, watch, _fields);
      const setSubmitting = setSubmittingFactory();
      const submitHandler = async (values: T) => {
         const errors = await validate?.(values);

         if (!isEmpty(errors)) {
            setErrors(errors);
            return errors;
         }

         return onSubmit(values, {
            setErrors,
            setSubmitting,
            resetForm: reset,
            setStatus,
         });
      };

      const _onError = (errors: FieldErrors<T>, event?: React.BaseSyntheticEvent) => {
         // const a = LdapSettings.validate(hookFormContext.getValues());
         console.error(errors, event);
         const errorList = [] as ErrorMessageOut[];
         combineYupErrorsMessages(errorList, errors, '');
         console.error(errorList);
         addToast(
            `Form is not valid. \n${errorList
               .map((e) => `${registered[e.name] || e.name}: ${e.message}`)
               .join('\n')} \nIf you have any difficulties please contact our support.`,
            {appearance: 'warning'}
         );

         return onError?.(errors, event);
      };

      return handleSubmit(submitHandler, _onError)(event);
   };

   React.useEffect(() => {
      if (enableReinitialize) {
         // @ts-ignore
         reset(cloneDeep(initialValues));
      }
   }, [reset, enableReinitialize, initialValues]);

   React.useEffect(() => {
      initialTouched?.forEach((f) => {
         // const val = watch(f) as any;
         const val = get(initialValues, f);
         // console.debug('setTouched:', f, val);
         // setValue(f, null, SetValueOptions);
         setValue(f, val!, SetValueOptions);
      });
   }, [initialTouched, setValue, initialValues]);

   return (
      <FormStatusContextProvider value={statusState}>
         <FormProvider {...methods} reset={reset}>
            <FormContextProvider value={formContext}>
               <form onSubmit={_onSubmit} ref={formRef}>
                  {children}
               </form>
            </FormContextProvider>
         </FormProvider>
      </FormStatusContextProvider>
   );
}

export default Formik;
